import { useContext, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../redux/store";
import { Button } from "./button/Button";
import { ModalContext, useModal } from "./Modal";
import LoadingOverlay from "./LoadingOverlay";

export interface InputPopupField {
  label: string;
  type: string;
  id: string;
  options?: Array<{ label: string; value: string }>;
  value: string;
  maxLength?: number;
  errorMessage?: string;
}

interface InputPopupProps {
  title: string;
  saveAction: (title: string, fields: InputPopupField[]) => void;
  fields: InputPopupField[];
  closeAction: () => void;
  enableLoading?: boolean;
  isLoading?: boolean;
}

const InputPopUp = ({
  title,
  saveAction,
  fields,
  closeAction,
  isLoading = false,
  enableLoading = false,
}: InputPopupProps) => {
  const modalContext = useContext(ModalContext);
  const [updatedFields, setFields] = useState<InputPopupField[]>(fields);
  const close = modalContext ? modalContext.close : () => {};

  const handleInputChange = (index: number, value: string) => {
    const updatedFields = [...fields];
    updatedFields[index].value = value; // Update the specific field's value
    updatedFields[index].errorMessage = "";
    setFields(updatedFields); // Update the state
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value;
    const updatedFields = [...fields];
    updatedFields[0].value = newValue; // Update the specific field's value
    updatedFields[0].errorMessage = "";
    setFields(updatedFields); // Update the state
  };

  const handleApply = () => {
    let hasError = false;
    const validatedFields = fields.map((field) => {
      if (field.id === "description") {
        return { ...field, errorMessage: "" };
      }
      if (!field.value.trim()) {
        hasError = true;
        return { ...field, errorMessage: `${field.label} is required` };
      }
      return { ...field };
    });

    setFields(validatedFields); // Update the fields with error messages if any

    if (!hasError) {
      saveAction(title, validatedFields); // Proceed with save action if no errors
    }
  };

  return (
    <div className="app-dialog-container">
      {enableLoading && <LoadingOverlay show={isLoading} />}
      <div className="dialog-container dialog-box-min filter-box">
        <div className="dialog-header"></div>
        <div className="dialog-title">
          <h3>{title} </h3>
        </div>
        <div className="dialog-body">
          {updatedFields.map((field, index) => (
            <div key={index} className="input-group">
              <label htmlFor={field.id}>{field.label}</label>
              {field.type === "select" ? (
                <select
                  id={field.id}
                  className="input-field"
                  onChange={(e) => handleSelectChange(e)}
                  value={field.value}
                >
                  {field.options?.map((option, idx) => (
                    <option key={idx} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  style={{ fontWeight: "normal" }}
                  type={field.type}
                  id={field.id}
                  className="input-field"
                  value={field.value}
                  maxLength={field.maxLength ? field.maxLength : 50}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                />
              )}
              {field.errorMessage && (
                <div
                  style={{ color: "red", fontSize: "12px", marginTop: "5px" }}
                >
                  {field.errorMessage}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="dialog-footer">
          <div className="apply-cancel">
            <Button
              color="primary"
              className="btn md"
              variant="solid"
              id="btn-apply"
              onClick={handleApply} // Validate fields on Apply button click
            >
              Apply
            </Button>
            <Button
              color="primary"
              className="btn md"
              variant="outline"
              id="btn-close"
              onClick={closeAction}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InputPopUp;
